'use client'

import { validateMultipleCustomDataFields } from '@repo/validation/customDataFieldValidation'
import { CustomDataField } from '../CustomDataField/CustomDataField'
import { CustomDataPageHeading } from '@repo/custom-data-pages/CustomDataPageHeading'
import { CustomDataSaveButtons } from '@repo/custom-data-pages/CustomDataSaveButtons'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { useCustomDataPageStore } from '@repo/store/useCustomDataPageStore'
import { useEffect, useState, useCallback } from 'react'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useNavigate } from 'react-router-dom'
import { ValidationErrorAlert } from '@repo/ui/ValidationErrorAlert'
import MUIStack from '@mui/material/Stack'
import PropTypes from 'prop-types'

export const CustomDataPage = ({
  pageId,
  pageTitle,
  fields,
  clientNo,
  matterNo,
  isOpen = false,
  setPageIsOpen,
  allowMultipleEntries = false
}) => {
  const { allowedActions } = useMenuItemsStore()
  const allowEditCustomData = allowedActions.editCustomData?.allowed || false
  const navigate = useNavigate()

  const [savedValues, setSavedValues] = useState({})
  const [editedValues, setEditedValues] = useState({})
  const [valuesLoaded, setValuesLoaded] = useState(false)
  const [pageIsInEditMode, setPageIsInEditMode] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  const [validationMessage, setValidationMessage] = useState(null)
  const [errorCount, setErrorCount] = useState(0)
  const {
    fetchCustomDataPageFieldValues,
    updateCustomDataPageFieldValues,
    fetchListValues,
    applyCustomDataFieldDefaultsAndFormatting
  } = useCustomDataPageStore()

  const toggleEditMode = (e) => {
    if (e) e.stopPropagation()
    setPageIsInEditMode((prev) => !prev)
  }

  const onUpdateValue = ({ field, value }) => {
    const updatedFieldValues = { ...editedValues, [field.customDataFieldId || field.fieldId]: value }
    setEditedValues(updatedFieldValues)
  }

  const onSave = async (e, isInline = false, fieldId) => {
    if (e?.stopPropagation) e.stopPropagation()
    if (e?.preventDefault) e.preventDefault()

    if (!allowEditCustomData) return

    let values = { ...editedValues }

    if (isInline && fieldId) {
      // if saving inline, we only want to save the one field that was edited
      values = { ...savedValues, [fieldId]: editedValues[fieldId] }
    }

    const editedValuesWithDefaultsAndFormatting = applyCustomDataFieldDefaultsAndFormatting({
      values,
      fields
    })

    const { allValidationErrors, errorCount } = await validateMultipleCustomDataFields({
      fields,
      values: editedValuesWithDefaultsAndFormatting
    })

    if (Object.keys(allValidationErrors).length > 0) {
      console.log('==================== VALIDATION ERRORS')
      console.log(allValidationErrors)
      setValidationErrors(allValidationErrors)
      setValidationMessage(null)
      setErrorCount(errorCount)
      return false
    }

    try {
      console.log('==================== SAVING FORM')

      const payload = [
        {
          customDataPageId: pageId,
          clientNo,
          matterNo,
          customDataPageFieldValues: Object.entries(editedValuesWithDefaultsAndFormatting).map(([key, value]) => {
            return {
              fieldId: key,
              value
            }
          })
        }
      ]

      await updateCustomDataPageFieldValues({ valueArray: payload })
      await fetchData(fieldId, editedValues)
      setValidationErrors({})
      setValidationMessage(null)
      setErrorCount(0)
      if (!isInline) toggleEditMode()
      return true
    } catch (err) {
      console.error(err)

      if (err?.status === 400) {
        // this is if the API has a validation error. Ideally, this will never happen if the frontend validation is working
        const { message } = err?.response?.data || {}
        setValidationMessage(message || null)
        setErrorCount(1)
      }

      return false
    }
  }

  const onCancel = async (e, isInline = false, fieldId) => {
    if (e?.stopPropagation) e.stopPropagation()
    if (isInline && fieldId) {
      // if canceling inline we don't want to reset the whole page, just the one field that was edited
      setEditedValues({ ...editedValues, [fieldId]: savedValues[fieldId] })
    } else {
      setEditedValues({ ...savedValues })
    }
    setValidationErrors({})
    setValidationMessage(null)
    setErrorCount(0)
    if (!isInline) toggleEditMode()
  }

  const fetchData = useCallback(
    async (fieldId, currentEditedValues) => {
      try {
        if (!clientNo || !pageId || !fields) return

        console.log('==================== FETCHING LIST VALUES DATA')
        const customDataListIds = fields
          .filter((field) => field.fieldType === 'List')
          .map((field) => field.customDataFieldListId)
        await fetchListValues({ customDataListIds })

        console.log('==================== FETCHING FIELD VALUES DATA')

        const { customDataPageFieldValues } = await fetchCustomDataPageFieldValues({
          clientNo,
          matterNo,
          pageId,
          fields
        })
        const fieldValues = {}
        fields?.forEach((field) => {
          const key = field.fieldId || field.customDataFieldId
          const valueItem = customDataPageFieldValues?.find(
            (item) => item.fieldId === key || item.customDataFieldId === key
          )

          const value = valueItem?.value || valueItem?.value === '0' || valueItem?.value === 0 ? valueItem?.value : null
          fieldValues[key] = value
        })

        setSavedValues({ ...fieldValues })

        if (fieldId && currentEditedValues) {
          // this is the case if we've saved an inline field, we don't want to update the edited values so we take existing values and just override the one that was edited
          setEditedValues({ ...currentEditedValues, [fieldId]: fieldValues[fieldId] })
        } else {
          // this is the case if we've saved the full page, so we should overwrite all the edited values
          setEditedValues({ ...fieldValues })
        }

        setValuesLoaded(true)
      } catch (error) {
        console.error(error)
        setSavedValues({})
        setEditedValues({})
      }
    },
    [clientNo, matterNo, pageId, fields, fetchCustomDataPageFieldValues, fetchListValues]
  )

  useEffect(() => {
    if (isOpen) {
      // we add a small delay to allow the page to be open first
      setTimeout(fetchData, 300)
    }
  }, [isOpen, fetchData])

  const goToMultiEntryPage = () => {
    navigate(`/case-management/matters/custom-data-pages/multi-entry?pageid=${pageId}&allowmultipleentries=true`)
  }

  return (
    <MUIStack
      data-testid="CustomDataPage"
      direction="column"
      spacing={6}
      id={`custom-data-page-${pageId}`}
      sx={{
        margin: 0,
        padding: '25px 20px',
        background: 'white',
        borderRadius: 2
      }}
    >
      <CustomDataPageHeading
        title={pageTitle || ''}
        id={pageId ? pageId : ''}
        toggleEditMode={toggleEditMode}
        onToggleOpen={() => setPageIsOpen({ pageId, isOpen: !isOpen })}
        onSave={onSave}
        onCancel={onCancel}
        isOpen={isOpen}
        pageIsInEditMode={pageIsInEditMode}
        valuesLoaded={valuesLoaded}
      />

      {allowEditCustomData ? (
        <ValidationErrorAlert errorCount={errorCount} isTop validationMessage={validationMessage} />
      ) : null}

      {fields && clientNo && isOpen ? (
        <MUIStack direction="column" spacing={6} alignItems="flex-end">
          <MUIStack
            direction="column"
            className="fieldList"
            spacing={0}
            sx={{
              borderRadius: '6px',
              width: '100%',
              backgroundColor: pageIsInEditMode ? '#DFD1B655' : 'transparent'
            }}
          >
            {fields.map((field) => {
              const key = field.fieldId || field.customDataFieldId

              return (
                <CustomDataField
                  key={key}
                  field={field}
                  valuesLoaded={valuesLoaded}
                  value={editedValues[key]}
                  pageIsInEditMode={pageIsInEditMode}
                  onSaveInline={onSave}
                  onCancelInline={onCancel}
                  onUpdateValue={onUpdateValue}
                  validationErrors={validationErrors[key]}
                />
              )
            })}
          </MUIStack>

          {allowMultipleEntries ? (
            <TextLinkButton
              onClick={goToMultiEntryPage}
              title={'This page supports multiple entries. Click here to view, add or edit other entries.'}
              sx={{ mt: 1, ml: '34px', width: 'calc(100% - 34px)', textAlign: 'left' }}
            />
          ) : null}

          {isOpen && pageIsInEditMode && valuesLoaded && allowEditCustomData ? (
            <>
              <ValidationErrorAlert errorCount={errorCount} validationMessage={validationMessage} />
              <CustomDataSaveButtons onSave={onSave} onCancel={onCancel} />
            </>
          ) : null}
        </MUIStack>
      ) : null}
    </MUIStack>
  )
}

CustomDataPage.propTypes = {
  pageId: PropTypes.string,
  pageTitle: PropTypes.string,
  fields: PropTypes.array,
  clientNo: PropTypes.string,
  matterNo: PropTypes.number,
  isOpen: PropTypes.bool,
  setPageIsOpen: PropTypes.func,
  allowMultipleEntries: PropTypes.bool
}
